import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Link } from "@mui/material";

import { useCurrentOrg } from "@/providers/auth/hooks";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { AgentThreadErrorContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import { OrganizationTier } from "@/shared/domain/orgs";

import styles from "./AssistantErrorContent.module.css";

// Matches the backend wire code set on AI-credit-cap refusals
// (roboto_service.worker.operations.ai_chat). Switching on it lets the web UI
// surface an upgrade affordance for the quota case without a schema change.
const AI_CREDIT_LIMIT_EXCEEDED_ERROR_CODE = "AI_CREDIT_LIMIT_EXCEEDED";
const PRICING_URL = "https://www.roboto.ai/pricing/";

interface AssistantErrorContentProps {
  error: AgentThreadErrorContent;
}

/**
 * Component for rendering error content in thread messages.
 * Displays an error icon and user-friendly error message. When a free org hits
 * the AI-credit cap, also offers a demo-request link to upgrade; premium orgs
 * (whose message tells them to contact their admin) get no upgrade CTA.
 */
export const AssistantErrorContent = ({
  error,
}: AssistantErrorContentProps) => {
  const currentOrg = useCurrentOrg();
  // Only free orgs can self-serve an upgrade. The backend sends the same error
  // code for both tiers (only the message text differs), so gate the CTA on tier
  // here, failing closed (no CTA) when the org is premium or not yet loaded.
  const showUpgradeCta =
    error.error_code === AI_CREDIT_LIMIT_EXCEEDED_ERROR_CODE &&
    currentOrg?.tier === OrganizationTier.Free;

  return (
    <div className={styles.indentedContainer}>
      <div className={styles.content}>
        <div className={styles.messageContainer}>
          <ErrorOutlineIcon className={styles.errorIcon} />
          <RobotoTypography className={styles.text}>
            {error.error_message}
          </RobotoTypography>
        </div>
        {showUpgradeCta && (
          <Link
            className={styles.upgradeLink}
            href={PRICING_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Request a demo to upgrade
          </Link>
        )}
      </div>
    </div>
  );
};
